import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { makeAPI } from "../../services/api";
import "./sandbox.css";
import { showError , showSuccess,showInfo} from '../../utils/utils';


function SandboxIcon({ children }) {
  return (
    <svg
      aria-hidden="true"
      className="sandbox-btn-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

export default function Sandbox({
  unityUrl = "http://localhost:5173/games/5_gzip/index.html",
  width = "100%",
  height = "650px",
  title = "WebGL Sandbox",
}) {
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const rootRef = useRef(null);
  const logListRef = useRef(null);
  const focusTimeoutRef = useRef(null);
  const [logs, setLogs] = useState([]);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const [status, setStatus] = useState("Loading");
  const [shareStatus, setShareStatus] = useState("");

  // const [searchParams] = useSearchParams();
  // const buildUrlParam = searchParams.get("build_url");
  // const unityUrlWithParam = buildUrlParam ? decodeURIComponent(buildUrlParam) : unityUrl;
  // const User_id = searchParams.get("user_id");

  const query = new URLSearchParams(useLocation().search);
  const buildUrlParam = query.get("build_url");
  const unityUrlWithParam = buildUrlParam ? decodeURIComponent(buildUrlParam) : unityUrl;
  const User_id = query.get("user_id");

  const User = JSON.parse(localStorage.getItem("user"));
  const [Publish_stat,setPublish_stat] = useState(true);
  const [User_,setUser]= useState('');
  const [GameData,setGameData] = useState({});

  const sub_id  = buildUrlParam ? buildUrlParam.split("/").slice(-2, -1)[0] : null;

  // useEffect(() => {
  //   console.log("Sandbox Params:", { buildUrlParam, unityUrlWithParam, User_id, User });
  // }, [buildUrlParam, unityUrlWithParam, User_id, User]);

  const addLog = (level, message) => {
    setLogs((prev) => [...prev, { time: new Date().toLocaleTimeString(), level, message }].slice(-200));
  };

  const callIframeController = (callback) => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return false;

    try {
      const controller = iframeWindow.__sandboxController;
      if (!controller) return false;
      callback(controller, iframeWindow);
      return true;
    } catch (err) {
      addLog("WARN", `[sandbox] iframe control failed: ${err?.message || err}`);
      return false;
    }
  };

  const sendToIframe = (cmd, payload = {}) => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) {
      addLog("WARN", "Iframe not ready yet.");
      return;
    }

    try {
      iframe.contentWindow.postMessage({ source: "sandbox", type: cmd, ...payload }, "*");
      addLog("INFO", `Sent: ${cmd}`);
    } catch (ex) {
      addLog("ERROR", `Failed to send ${cmd}: ${ex.message}`);
    }
  };

  useEffect(() => {
    const originalConsole = {
      log: window.console.log,
      info: window.console.info,
      warn: window.console.warn,
      error: window.console.error,
      debug: window.console.debug,
    };

    const wrapConsole = (level, fn) => {
      return (...args) => {
        try {
          const text = args
            .map((arg) => {
              if (typeof arg === "string") return arg;
              if (arg instanceof Error) return arg.stack || arg.message;
              try {
                return JSON.stringify(arg);
              } catch {
                return String(arg);
              }
            })
            .join(" ");
          addLog(level, text);
        } catch (err) {
          addLog("ERROR", `[console] format failed: ${err?.message || err}`);
        }

        try {
          fn?.apply(window?.console, args);
        } catch {
          // no-op
        }
      };
    };

    window.console.log = wrapConsole("INFO", originalConsole.log);
    window.console.info = wrapConsole("INFO", originalConsole.info);
    window.console.warn = wrapConsole("WARN", originalConsole.warn);
    window.console.error = wrapConsole("ERROR", originalConsole.error);
    window.console.debug = wrapConsole("DEBUG", originalConsole.debug);

    addLog("INFO", "[sandbox] console capture installed");

    const handleUnhandled = (event) => {
      addLog("ERROR", `Unhandled error: ${event.message || event.error?.message || event.type}`);
    };

    const handleUnhandledRejection = (event) => {
      const { reason } = event;
      const message =
        reason instanceof Error
          ? reason.stack || reason.message
          : (() => {
            try {
              return JSON.stringify(reason);
            } catch {
              return String(reason);
            }
          })();

      addLog("ERROR", `Unhandled rejection: ${message}`);
    };

    window.addEventListener("error", handleUnhandled);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.console.log = originalConsole.log;
      window.console.info = originalConsole.info;
      window.console.warn = originalConsole.warn;
      window.console.error = originalConsole.error;
      window.console.debug = originalConsole.debug;
      window.removeEventListener("error", handleUnhandled);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.data) return;
      const data = typeof event.data === "string" ? event.data : event.data;

      if (typeof data === "object" && data.source === "unity-webgl") {
        if (data.type === "log") {
          addLog("INFO", data.text || "Unity log");
        } else if (data.type === "warn") {
          addLog("WARN", data.text || "Unity warning");
        } else if (data.type === "error") {
          addLog("ERROR", data.text || "Unity error");
        } else if (data.type === "status") {
          setStatus(data.text || "Unity status");
          addLog("INFO", `[status] ${data.text || "Unity status"}`);
        }
      } else if (typeof data === "object" && data.source === "iframe-console") {
        addLog(data.level || "INFO", data.text || "iframe console");
      } else if (typeof data === "string") {
        if (data.startsWith("UnityLog:")) {
          addLog("INFO", data.replace("UnityLog:", ""));
        } else if (data.startsWith("UnityStatus:")) {
          const nextStatus = data.replace("UnityStatus:", "");
          setStatus(nextStatus);
          addLog("INFO", `[status] ${nextStatus}`);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    const injectConsoleBridge = () => {
      const iframeWindow = iframeRef.current?.contentWindow;
      if (!iframeWindow) return;

      try {
        const originalConsole = {
          log: iframeWindow.console.log.bind(iframeWindow.console),
          info: iframeWindow.console.info.bind(iframeWindow.console),
          warn: iframeWindow.console.warn.bind(iframeWindow.console),
          error: iframeWindow.console.error.bind(iframeWindow.console),
        };

        const makeWrapper = (level, fn) => (...args) => {
          const text = args
            .map((arg) => {
              if (typeof arg === "string") return arg;
              if (arg instanceof Error) return arg.stack || arg.message;
              try {
                return JSON.stringify(arg);
              } catch {
                return String(arg);
              }
            })
            .join(" ");

          window.parent.postMessage({ source: "iframe-console", level, text }, "*");
          fn(...args);
        };

        iframeWindow.console.log = makeWrapper("INFO", originalConsole.log);
        iframeWindow.console.info = makeWrapper("INFO", originalConsole.info);
        iframeWindow.console.warn = makeWrapper("WARN", originalConsole.warn);
        iframeWindow.console.error = makeWrapper("ERROR", originalConsole.error);
      } catch (err) {
        addLog("WARN", `[sandbox] iframe console bridge injection failed: ${err?.message || err}`);
      }
    };

    const handleLoad = () => {
      injectConsoleBridge();
    };

    const iframeElement = iframeRef.current;
    if (iframeElement) {
      iframeElement.addEventListener("load", handleLoad);
      injectConsoleBridge();
    }

    return () => {
      if (iframeElement) iframeElement.removeEventListener("load", handleLoad);
    };
  }, [iframeKey]);

  useEffect(() => {
    const installIframeController = () => {
      const iframeWindow = iframeRef.current?.contentWindow;
      if (!iframeWindow) return false;

      try {
        const iframeDocument = iframeWindow.document;
        if (!iframeDocument) return false;

        const attachController = () => {
          if (iframeWindow.__sandboxControllerInstalled) return;

          iframeWindow.__sandboxControllerInstalled = true;
          iframeWindow.__sandboxManualPause = false;

          const getMediaElements = () => Array.from(iframeDocument.querySelectorAll("video, audio"));

          const getFocusTarget = () =>
            iframeDocument.querySelector("canvas, [tabindex], button, input, select, textarea") ||
            iframeDocument.body;

          const getModule = () => iframeWindow.Module;

          const tryModuleMethod = (methodName, value) => {
            try {
              const moduleRef = getModule();
              if (moduleRef && typeof moduleRef[methodName] === "function") {
                moduleRef[methodName](value);
                return true;
              }
            } catch {
              // DOM fallback will handle the rest
            }
            return false;
          };

          const getAudioContexts = () => {
            const contexts = new Set();
            const moduleRef = getModule();

            if (moduleRef?.SDL2?.audioContext) contexts.add(moduleRef.SDL2.audioContext);
            if (iframeWindow.WEBAudio?.audioContext) contexts.add(iframeWindow.WEBAudio.audioContext);
            if (iframeWindow.audioContext) contexts.add(iframeWindow.audioContext);

            return Array.from(contexts).filter(Boolean);
          };

          const focusGame = () => {
            const target = getFocusTarget();
            if (!target) return false;

            try {
              if (target !== iframeDocument.body && !target.hasAttribute("tabindex")) {
                target.setAttribute("tabindex", "-1");
              }
              target.focus({ preventScroll: true });
              iframeWindow.focus();
              return true;
            } catch {
              return false;
            }
          };

          const setPlaying = (playing) => {
            iframeWindow.__sandboxManualPause = !playing;

            getMediaElements().forEach((media) => {
              try {
                if (playing) {
                  media.play?.().catch(() => { });
                } else {
                  media.pause?.();
                }
              } catch {
                // ignore per-element control errors
              }
            });

            const moduleRef = getModule();
            if (playing) {
              if (typeof moduleRef?.resumeMainLoop === "function") {
                moduleRef.resumeMainLoop();
              } else if (!tryModuleMethod("SetPause", 0)) {
                tryModuleMethod("Pause", 0);
              }
            } else if (typeof moduleRef?.pauseMainLoop === "function") {
              moduleRef.pauseMainLoop();
            } else if (!tryModuleMethod("SetPause", 1)) {
              tryModuleMethod("Pause", 1);
            }

            if (playing) {
              focusGame();
            }
          };

          const setMuted = (muted) => {
            getMediaElements().forEach((media) => {
              try {
                media.muted = muted;
                media.defaultMuted = muted;
                if (!muted && media.volume === 0) {
                  media.volume = 1;
                }
              } catch {
                // ignore per-element control errors
              }
            });

            getAudioContexts().forEach((audioContext) => {
              try {
                if (muted && audioContext.state !== "suspended") {
                  audioContext.suspend?.().catch?.(() => { });
                } else if (!muted && audioContext.state === "suspended") {
                  audioContext.resume?.().catch?.(() => { });
                }
              } catch {
                // ignore per-context control errors
              }
            });

            tryModuleMethod("SetMute", muted ? 1 : 0);
          };

          const requestGameFullscreen = async () => {
            const gameTarget =
              iframeDocument.querySelector("canvas") ||
              iframeDocument.documentElement;

            if (tryModuleMethod("SetFullscreen", 1)) return true;
            if (gameTarget?.requestFullscreen) {
              await gameTarget.requestFullscreen();
              return true;
            }
            return false;
          };

          iframeWindow.__sandboxController = {
            focusGame,
            requestGameFullscreen,
            setMuted,
            setPlaying,
          };

          iframeWindow.addEventListener("message", (event) => {
            const data = event.data;
            if (!data || data.source !== "sandbox") return;

            if (data.type === "play") {
              iframeWindow.__sandboxController.setPlaying(true);
            } else if (data.type === "pause") {
              iframeWindow.__sandboxController.setPlaying(false);
            } else if (data.type === "mute") {
              iframeWindow.__sandboxController.setMuted(true);
            } else if (data.type === "unmute") {
              iframeWindow.__sandboxController.setMuted(false);
            } else if (data.type === "focus") {
              iframeWindow.__sandboxController.focusGame();
            } else if (data.type === "fullscreen") {
              iframeWindow.__sandboxController.requestGameFullscreen().catch(() => { });
            }
          });

          iframeWindow.addEventListener("blur", () => {
            if (iframeWindow.__sandboxManualPause) return;

            iframeWindow.setTimeout(() => {
              if (!iframeWindow.__sandboxManualPause) {
                iframeWindow.__sandboxController.focusGame();
              }
            }, 0);
          });

          iframeDocument.addEventListener("visibilitychange", () => {
            if (iframeDocument.visibilityState === "visible" && !iframeWindow.__sandboxManualPause) {
              iframeWindow.setTimeout(() => {
                iframeWindow.__sandboxController.focusGame();
              }, 0);
            }
          });

          iframeWindow.parent.postMessage({ source: "unity-webgl", type: "status", text: "Ready" }, "*");
        };

        if (iframeDocument.readyState === "interactive" || iframeDocument.readyState === "complete") {
          attachController();
        } else {
          iframeDocument.addEventListener("DOMContentLoaded", attachController, { once: true });
        }

        return true;
      } catch (err) {
        addLog("WARN", `[sandbox] iframe controller injection failed: ${err?.message || err}`);
        return false;
      }
    };

    const handleLoad = () => {
      const installed = installIframeController();
      if (installed) {
        setStatus("Ready");
        setIsPlaying(true);
        sendToIframe("focus");
      }
    };

    const iframeElement = iframeRef.current;
    if (iframeElement) {
      iframeElement.addEventListener("load", handleLoad);
      installIframeController();
    }

    return () => {
      if (iframeElement) iframeElement.removeEventListener("load", handleLoad);
    };
  }, [iframeKey]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const active = Boolean(document.fullscreenElement);
      setStatus(active ? "In fullscreen" : isPlaying ? "Playing" : "Paused");
      addLog("INFO", active ? "Entered fullscreen" : "Exited fullscreen");
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, [isPlaying]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const restoreFocus = (event) => {
      if (!isPlaying) return;
      if (event.target?.closest(".sandbox-toolbar")) return;
      if (event.target?.closest(".sandbox-log-footer")) return;

      window.clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = window.setTimeout(() => {
        callIframeController((controller) => {
          controller.focusGame();
        });
        sendToIframe("focus");
      }, 0);
    };

    root.addEventListener("pointerdown", restoreFocus);
    return () => {
      root.removeEventListener("pointerdown", restoreFocus);
      window.clearTimeout(focusTimeoutRef.current);
    };
  }, [isPlaying]);

  useEffect(() => {
    const logList = logListRef.current;
    if (!logList) return;

    logList.scrollTop = logList.scrollHeight;
  }, [logs]);

  const onPlay = () => {
    setIsPlaying(true);
    callIframeController((controller) => {
      controller.setPlaying(true);
      controller.focusGame();
    });
    sendToIframe("play");
    sendToIframe("focus");
    setStatus("Playing");
  };

  const onPause = () => {
    setIsPlaying(false);
    callIframeController((controller) => {
      controller.setPlaying(false);
    });
    sendToIframe("pause");
    setStatus("Paused");
  };

  const onMute = () => {
    setIsMuted(true);
    callIframeController((controller) => {
      controller.setMuted(true);
    });
    sendToIframe("mute");
    addLog("INFO", "Muted");
  };

  const onUnmute = () => {
    setIsMuted(false);
    callIframeController((controller) => {
      controller.setMuted(false);
    });
    sendToIframe("unmute");
    addLog("INFO", "Unmuted");
  };

  const onReload = () => {
    setIframeKey((prev) => prev + 1);
    setIsPlaying(true);
    setIsMuted(false);
    setStatus("Reloading");
    addLog("INFO", "Reloaded iframe");
  };

  const onFullscreen = async () => {
    if (!containerRef.current) return;

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        const handledByGame = await new Promise((resolve) => {
          const controllerAvailable = callIframeController((controller) => {
            Promise.resolve(controller.requestGameFullscreen())
              .then((result) => resolve(Boolean(result)))
              .catch(() => resolve(false));
          });

          if (!controllerAvailable) {
            resolve(false);
          }
        });

        if (!handledByGame && containerRef.current.requestFullscreen) {
          await containerRef.current.requestFullscreen();
        }
      }

      addLog("INFO", "Toggled fullscreen");
    } catch (err) {
      addLog("ERROR", `Fullscreen failed: ${err.message}`);
    }
  };

  const onShare = async () => {
    const link = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "GameJam",
          text: "Check this game!",
          url: window.location.href
        });
        addLog("INFO", `Share link copied: ${link}`);
      window.setTimeout(() => setShareStatus(""), 1800);
      } catch (err) {
        console.log(err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      // alert("Link copied to clipboard!");
      setShareStatus("Copy failed; use manual copy");
      addLog("ERROR", `Share copy failed: ${err.message}`);
    } 
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const logRows = useMemo(() => {
    return logs.map((entry, idx) => (
      <div key={`${entry.time}-${idx}`} className="sandbox-log-row">
        <span className="sandbox-log-time">[{entry.time}]</span>
        <strong
          className="sandbox-log-level"
          style={{ color: entry.level === "ERROR" ? "#ff6" : entry.level === "WARN" ? "#f90" : "#9ef" }}
        >
          {entry.level}
        </strong>
        <span style={{ marginLeft: 8, color: "#f5f5f5" }}>{entry.message}</span>
      </div>
    ));
  }, [logs]);

  const OpenModal = () => {
    if (window.UIkit && typeof window.UIkit.modal === "function") {
        window.UIkit.modal("#modal-login", { container: false }).show();
    }
};
  const PublishyourGame = async (stat) => {
    if (!User_.user_id) {
      // alert("Please log in to publish your game.");
      
      OpenModal();
      setShareStatus("Please log in to publish your game.");
      return;
    }
    let data = { build_url: buildUrlParam, publish_stat: stat, user_id: User_?.user_id };


    try {
      const PublishGame = await makeAPI("/api/gamejam_publish", "POST", false, data);
      if(PublishGame.status === "S"){
        setPublish_stat(stat ? false :true);
       stat ? showSuccess("Published..!") : showError("Unpublished");
       localStorage.setItem(`game_publish_stat_${sub_id}`, stat ? "published" : "unpublished");
       addLog("INFO", stat ? "Game published" : "Game unpublished");
      }
    } catch (error) {
      console.log("ERROR",error);
    }
  };

  const GetUserData = async () => {

    try {
      const get_user_data = await makeAPI('/api/get_user_data','post',true,{});
      if(get_user_data.status === "S"){
        setUser(get_user_data.data);
      }
    } catch (error) {
      console.log("ERROR",error);
    }

  }
  const GameListingApi = async () => {

    try {
        let GameData = await makeAPI('/api/gamejam_details','post',false,{ sub_id: sub_id, user_id: User_?.user_id });
        if(GameData.status === "S"){
            setGameData(GameData.data);
            console.log("GameData",GameData.data);
        }
    } catch (error) {
      console.log("ERROR",error);
    }

  }

  useEffect(()=>{
    GetUserData();
  },[]);

  useEffect(() => {
    if (!sub_id) return;
    GameListingApi();
  }, [sub_id, User_?.user_id]);

  return (
    <>
    <div ref={rootRef} className="sandbox-root">
      <div className="sandbox-header">
        <div>
          <div className="sandbox-title">{GameData?.game_name ||title}</div>
          <div className="sandbox-subtitle">game sandbox + debug console</div>
        </div>
        <div className="sandbox-status-wrap">
          {Number(User_id) === Number(User?.user_id) ?
            <button className="sandbox-btn" style={{ backgroundColor: "#ff810e" }}
              onClick={() => {
                window.history.back();
              }}
            ><i className="fa-solid fa-arrow-left" />&nbsp;Back
            </button> : null}
          <span className={Number(User_id) === Number(User?.user_id) ? "" : "sandbox-status"}>
            {/* {console.log(localStorage.getItem(`game_publish_stat_${sub_id}`),"publish status in sandbox")} */}
            {Number(User_id) === Number(User?.user_id) ?     
            ( (localStorage.getItem(`game_publish_stat_${sub_id}`) === "unpublished") || (localStorage.getItem(`game_publish_stat_${sub_id}`) ===  null && GameData?.publish_stat === false) ? <button className="sandbox-btn" style={{ backgroundColor: "#ff810e" }}
              onClick={() => {
                PublishyourGame(true);
              }}
            >Publish<i className="fa-solid fa-upload" />
            </button> : <button className="sandbox-btn" style={{ backgroundColor: "#ff810e" }}
              onClick={() => {
                PublishyourGame(false);
              }}
            >Unpublish<i className="fa-solid fa-upload" />
            </button> ) : status}
          </span>
          {shareStatus ? <span className="sandbox-share">{shareStatus}</span> : null}
        </div>
      </div>

      <div className="sandbox-layout">
        <div className="sandbox-game-panel">
          <div
            ref={containerRef}
            className="sandbox-iframe-wrap"
            style={{ width, minHeight: height }}
          >
            <iframe
              key={iframeKey}
              ref={iframeRef}
              src={unityUrlWithParam}
              title="Unity WebGL Sandbox"
              className="sandbox-iframe"
              tabIndex={-1}
              // allowFullScreen
              allow="fullscreen; autoplay; clipboard-read; clipboard-write"
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            />
          </div>

          <div className="sandbox-toolbar">
            <button
              type="button"
              className="sandbox-btn"
              onClick={isPlaying ? onPause : onPlay}
              aria-label={isPlaying ? "Pause game" : "Play game"}
              title={isPlaying ? "Pause game" : "Play game"}
            >
              {isPlaying ? (
                <SandboxIcon>
                  <line x1="9" y1="5" x2="9" y2="19" />
                  <line x1="15" y1="5" x2="15" y2="19" />
                </SandboxIcon>
              ) : (
                <SandboxIcon>
                  <polygon points="8 5 19 12 8 19 8 5" fill="currentColor" stroke="none" />
                </SandboxIcon>
              )}
            </button>
            <button
              type="button"
              className="sandbox-btn"
              onClick={isMuted ? onUnmute : onMute}
              aria-label={isMuted ? "Unmute game" : "Mute game"}
              title={isMuted ? "Unmute game" : "Mute game"}
            >
              {isMuted ? (
                <SandboxIcon>
                  <polygon points="4 9 8 9 13 5 13 19 8 15 4 15 4 9" fill="currentColor" stroke="none" />
                  <line x1="17" y1="9" x2="21" y2="15" />
                  <line x1="21" y1="9" x2="17" y2="15" />
                </SandboxIcon>
              ) : (
                <SandboxIcon>
                  <polygon points="4 9 8 9 13 5 13 19 8 15 4 15 4 9" fill="currentColor" stroke="none" />
                  <path d="M16 9a5 5 0 0 1 0 6" />
                  <path d="M18.5 6.5a8.5 8.5 0 0 1 0 11" />
                </SandboxIcon>
              )}
            </button>
            <button type="button" className="sandbox-btn" onClick={onReload} aria-label="Reload game" title="Reload game">
              <SandboxIcon>
                <path d="M20 11a8 8 0 1 0 2 5.3" />
                <polyline points="20 4 20 11 13 11" />
              </SandboxIcon>
            </button>
            <button type="button" className="sandbox-btn" onClick={onFullscreen} aria-label="Toggle fullscreen" title="Toggle fullscreen">
              <SandboxIcon>
                <polyline points="8 3 3 3 3 8" />
                <line x1="3" y1="3" x2="9" y2="9" />
                <polyline points="16 3 21 3 21 8" />
                <line x1="21" y1="3" x2="15" y2="9" />
                <polyline points="8 21 3 21 3 16" />
                <line x1="3" y1="21" x2="9" y2="15" />
                <polyline points="16 21 21 21 21 16" />
                <line x1="21" y1="21" x2="15" y2="15" />
              </SandboxIcon>
            </button>
            <button type="button" className="sandbox-btn" onClick={onShare} aria-label="Share game link" title="Share game link">
              <SandboxIcon>
                <circle cx="18" cy="5" r="3" fill="currentColor" stroke="none" />
                <circle cx="6" cy="12" r="3" fill="currentColor" stroke="none" />
                <circle cx="18" cy="19" r="3" fill="currentColor" stroke="none" />
                <line x1="8.7" y1="10.7" x2="15.3" y2="6.3" />
                <line x1="8.7" y1="13.3" x2="15.3" y2="17.7" />
              </SandboxIcon>
            </button>
          </div>
        </div>
        
        <div className="sandbox-panel">
          <div className="sandbox-panel-heading">
            <div className="panel-title">Control panel</div>
            <div className="panel-count">{logs.length} logs</div>
          </div>
          <div className="sandbox-logs-title">Logs</div>
          <div ref={logListRef} className="sandbox-log-list">
            {logRows.length ? logRows : <div className="sandbox-log-empty">Awaiting logs from Unity and console.</div>}
          </div>
          <div className="sandbox-log-footer">
            <button type="button" className="sandbox-btn sandbox-clear" onClick={clearLogs}>
              Clear Logs
            </button>
          </div>
        </div>
        
      </div>
      <div className="sandbox-details">
            <section className="sandbox-detail-card">
              <h4 className="sandbox-detail-title">Description</h4>
              <p className="sandbox-detail-text">{GameData?.description || "No description provided."}</p>
            </section>
            <section className="sandbox-detail-card">
              <h4 className="sandbox-detail-title">Instructions</h4>
              <p className="sandbox-detail-text">{GameData?.controls || "No controller instructions provided."}</p>
            </section>
        </div>
    </div>
    </>
  );
}
